import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Typography,
    Box,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CommunicationService } from 'src/services/customer/communication';
import { CommunicatinPageStyle } from 'src/theme/vemigoCustomStyles/VemigoCustomStyles';

interface NotificationCommunicationDialogProps {
    open: boolean;
    onClose: () => void;
    communicationId: number;
}

interface CommunicationData {
    id: number;
    date: string;
    conversation: string;
    customerFeedback: string;
    followUpDate?: string;
    followUpAction?: string;
    previousCommunicationId?: number;
    communicationTypeId?: number;
    communicationSubject?: {
        id: number;
        name: string;
    };
    customer?: {
        id: number;
        firstAndMiddleName: string;
        lastName?: string;
    };
}

export const NotificationCommunicationDialog: React.FC<NotificationCommunicationDialogProps> = ({
    open,
    onClose,
    communicationId
}) => {
    const [loading, setLoading] = useState(false);
    const [communicationData, setCommunicationData] = useState<CommunicationData | null>(null);
    const [previousCommunications, setPreviousCommunications] = useState<CommunicationData[]>([]);
    const [loadedIds, setLoadedIds] = useState<Set<number>>(new Set());
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

    useEffect(() => {
        if (open && communicationId) {
            fetchInitialCommunication();
        }
    }, [open, communicationId]);

    const fetchInitialCommunication = async () => {
        setLoading(true);
        try {
            const data = await CommunicationService.getFollowUp(communicationId);
            setCommunicationData(data);
            setPreviousCommunications([data]);
            setLoadedIds(new Set([data.id]));
            setExpandedIds(new Set());
        } catch (error) {
            console.error('Error fetching communication:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPreviousData = async (id: number) => {
        if (id !== 0 && !loadedIds.has(id)) {
            try {
                const previousData = await CommunicationService.getFollowUp(id);
                if (previousData) {
                    setPreviousCommunications((prev) => [previousData, ...prev]);
                    setLoadedIds((prevIds) => new Set(prevIds).add(id));
                    setExpandedIds((prevExpanded) => new Set(prevExpanded).add(id));
                }
            } catch (error) {
                console.error('Error fetching previous communication:', error);
            }
        }
    };

    const handleHideData = (id: number) => {
        setPreviousCommunications((prev) =>
            prev.filter((comm) => comm.previousCommunicationId !== id)
        );
        setExpandedIds((prevExpanded) => {
            const newSet = new Set(prevExpanded);
            newSet.delete(id);
            return newSet;
        });
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const handleClose = () => {
        setCommunicationData(null);
        setPreviousCommunications([]);
        setLoadedIds(new Set());
        setExpandedIds(new Set());
        onClose();
    };


    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth

        >
            <DialogTitle>
                Customer Inquiry - #
                {communicationData?.customer && (
                    <>
                        {communicationData.customer.firstAndMiddleName}
                    </>
                )}
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                        <CircularProgress />
                    </Box>
                ) : previousCommunications.length > 0 ? (
                    previousCommunications.map((comm, index) => (
                        <div key={index} style={{ position: 'relative', marginBottom: '1rem', borderBottom: '1px solid #ccc', paddingBottom: '1rem' }}>
                            <div style={CommunicatinPageStyle.date}>
                                <b>{formatDate(comm.date)}</b>
                            </div>

                            <div style={CommunicatinPageStyle.chatBubble}>
                                {comm.conversation || <i>No message</i>}
                            </div>

                            <div style={CommunicatinPageStyle.customerChat}>
                                {comm.customerFeedback || <i>No response</i>}
                            </div>

                            <div>
                                <b>Follow Up Date:</b>{" "}
                                {comm.followUpDate
                                    ? formatDate(comm.followUpDate)
                                    : <i>No Follow Up</i>}
                            </div>
                            <div style={CommunicatinPageStyle.followUp}>
                                {comm.followUpAction || <i>No follow up action</i>}
                            </div>
                            {comm.previousCommunicationId !== null &&
                                comm.previousCommunicationId !== undefined &&
                                comm.previousCommunicationId !== 0 &&
                                !loadedIds.has(comm.previousCommunicationId) && (
                                    <Button
                                        onClick={() => fetchPreviousData(comm.previousCommunicationId!)}
                                        variant="contained"
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            color: '#4F5AED',
                                            background: 'transparent',
                                            border: '1px solid #F0F1FA',
                                            padding: '5px 10px',
                                            borderRadius: '8px',
                                            boxShadow: 'none',
                                            fontWeight: 'normal',
                                            '&:hover': {
                                                background: '#F0F1FA',
                                                boxShadow: 'none'
                                            }
                                        }}
                                    >
                                        Previous Data
                                    </Button>
                                )}

                            {comm.previousCommunicationId !== null &&
                                comm.previousCommunicationId !== undefined &&
                                comm.previousCommunicationId !== 0 &&
                                expandedIds.has(comm.previousCommunicationId) && (
                                    <Button
                                        onClick={() => handleHideData(comm.previousCommunicationId!)}
                                        variant="contained"
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            color: '#4F5AED',
                                            background: 'transparent',
                                            border: '1px solid #F0F1FA',
                                            padding: '5px 10px',
                                            borderRadius: '8px',
                                            boxShadow: 'none',
                                            fontWeight: 'normal',
                                            '&:hover': {
                                                background: '#F0F1FA',
                                                boxShadow: 'none'
                                            }
                                        }}
                                    >
                                        Hide
                                    </Button>
                                )}
                        </div>
                    ))
                ) : (
                    <Box textAlign="center" py={4}>
                        <Typography color="text.secondary">
                            No communication data found
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                    variant="contained"
                >
                    Close Chats
                </Button>
            </DialogActions>
        </Dialog>
    );
};